<template>
  <div class="map-picker-root">
    <div class="map-search">
      <input
        v-model="searchText"
        type="text"
        placeholder="Entrez une adresse..."
        class="map-search-input"
        @keydown.enter="searchAddress"
      />
      <button type="button" class="btn btn-sm btn-cyan" :disabled="searching" @click="searchAddress">
        <Icon :name="searching ? 'refresh' : 'arrow'" :size="12" />
        {{ searching ? '...' : 'Chercher' }}
      </button>
      <button type="button" class="btn btn-sm btn-outline" title="Me localiser" @click="locateMe">
        <Icon name="location" :size="12" />
      </button>
      <button v-if="journeyPoints.length" type="button" class="map-overview" @click="fitJourney">Voir tous les points</button>
    </div>
    <div v-if="mapError" class="map-error">{{ mapError }}</div>
    <div ref="mapContainer" class="map-container"></div>
    <div class="map-hint">
      <span v-if="modelValue?.lat != null && modelValue?.lng != null">
        {{ modelValue.lat.toFixed(5) }}, {{ modelValue.lng.toFixed(5) }}
        <template v-if="modelValue?.region || modelValue?.country"> · {{ [modelValue.region, modelValue.country].filter(Boolean).join(' · ') }}</template>
      </span>
      <span v-else>Cliquez sur la carte ou cherchez une adresse pour placer le point.</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, type PropType } from 'vue';
import { state } from '../../stores/state.js';
import { gpsState } from '../../stores/gps.js';
import { socket } from '../../stores/socket.js';
import { reverseTerritoryFromCoordinates } from '../../domain/geography/reverseTerritory';
import Icon from './Icon.vue';
import { L, addOpenFreeMapLayer, OPENFREEMAP_ATTRIBUTION_HTML, type OpenFreeMapLayerHandle } from '../../engine/openFreeMap';

const props = defineProps({
  modelValue: { type: Object, default: null }, // { lat, lng }
  emoji: { type: String, default: '📍' },
  zoom: { type: Number, default: 16 },
  streetMap: { type: Boolean, default: false },
  resolveTerritory: { type: Boolean, default: false },
  journeyPoints: { type: Array as PropType<Array<{id:string;lat:number;lng:number;title:string}>>, default: () => [] },
  activePointId: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const mapContainer = ref(null);
const searchText = ref('');
const searching = ref(false);
const mapError = ref('');
let map: any = null;
let marker: any = null;
let journeyLayer: any = null;
let journeyFramed = false;
let baseMapLayer: OpenFreeMapLayerHandle | null = null;
let resizeObserver: any = null;
let reverseRequestId = 0;
let reverseTimer: ReturnType<typeof setTimeout> | null = null;

const DEFAULT_CENTER = [48.8566, 2.3522];

onMounted(() => {
  if (!mapContainer.value) return;
  initMap();
});

onUnmounted(() => {
  if (reverseTimer) clearTimeout(reverseTimer);
  if (resizeObserver) resizeObserver.disconnect();
  baseMapLayer?.remove();
  baseMapLayer = null;
  if (map) map.remove();
  map = null;
  marker = null;
});

watch(() => props.modelValue, (val) => {
  if (!map) return;
  if (props.journeyPoints.length) { renderJourney(); return; }
  if (val?.lat != null && val?.lng != null) {
    if (marker) marker.setLatLng([val.lat, val.lng]);
    else marker = createMarker(val.lat, val.lng);
  } else if (marker) {
    marker.remove();
    marker = null;
  }
}, { deep: true });
watch(() => [props.journeyPoints, props.activePointId], () => {
  if (map) renderJourney();
}, { deep: true });

function displayedPoints() {
  return props.journeyPoints.map((point, index) => ({
    ...point, number: index + 1,
    ...(point.id === props.activePointId ? props.modelValue || {} : {}),
  })).filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng)
    && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180);
}

function renderJourney() {
  marker?.remove(); marker = null;
  journeyLayer?.remove(); journeyLayer = L.layerGroup().addTo(map);
  const points = displayedPoints();
  const placed: Array<{x:number;y:number}> = [];
  const size = map.getSize();
  for (const point of points) {
    const active = point.id === props.activePointId;
    // Écarter les sceaux proches, sans déplacer leurs coordonnées enregistrées.
    const origin = map.latLngToContainerPoint([point.lat, point.lng]);
    let position = {x:origin.x,y:origin.y};
    if (origin.x >= 0 && origin.x <= size.x && origin.y >= 0 && origin.y <= size.y) {
      const candidates = [];
      for (let x=-6;x<=6;x++) for(let y=-6;y<=6;y++) candidates.push({x:x*50,y:y*50});
      candidates.sort((a,b) => a.x*a.x+a.y*a.y-b.x*b.x-b.y*b.y);
      for(const delta of candidates) {
        const candidate = {x:origin.x+delta.x,y:origin.y+delta.y};
        if(candidate.x<27 || candidate.x>size.x-27 || candidate.y<27 || candidate.y>size.y-38) continue;
        if(placed.some(p => Math.abs(p.x-candidate.x)<49 && Math.abs(p.y-candidate.y)<49)) continue;
        position=candidate; break;
      }
    }
    placed.push(position);
    const offset = {x:position.x-origin.x,y:position.y-origin.y};
    if(offset.x || offset.y) {
      L.polyline([[point.lat,point.lng],map.containerPointToLatLng([position.x,position.y])], {color:'#56391d',weight:2,dashArray:'3 3',interactive:false}).addTo(journeyLayer);
      L.circleMarker([point.lat,point.lng],{radius:3,color:'#56391d',fillOpacity:1,interactive:false}).addTo(journeyLayer);
    }
    const badge = document.createElement('span');
    badge.className = `journey-map-badge${active ? ' is-current' : ''}`;
    badge.textContent = String(point.number);
    const label = `Point ${point.number} · ${point.title || 'Nouvelle étape'}${active ? ' · En cours de placement' : ''}`;
    const pin = L.marker([point.lat, point.lng], {
      icon: L.divIcon({ className: 'journey-map-pin', html: badge, iconSize: [44,44], iconAnchor: [22-offset.x,22-offset.y] }),
      title: label, alt: label, zIndexOffset: active ? 1000 : 0,
    }).addTo(journeyLayer);
    const tooltip = document.createElement('span'); tooltip.textContent = label;
    pin.bindTooltip(tooltip, { direction: 'top', offset: [offset.x,offset.y-24] });
  }
}

function fitJourney() {
  const points = displayedPoints();
  if (map && points.length && map.getSize().x > 0 && map.getSize().y > 0) {
    map.fitBounds(points.map(p => [p.lat,p.lng]), { padding: [54,54], maxZoom: 18, animate: false });
    journeyFramed = true;
  }
}

function initMap() {
  if (!mapContainer.value) return; // démonté pendant l'attente de Leaflet
  const playerLoc = state.player?.location;
  const hasSelection = props.modelValue?.lat != null && props.modelValue?.lng != null;
  const center = hasSelection ? [props.modelValue.lat, props.modelValue.lng] : (playerLoc || DEFAULT_CENTER);
  const z = hasSelection ? Math.max(props.zoom, 17) : props.zoom;
  map = L.map(mapContainer.value, { zoomControl: false, attributionControl: true }).setView(center, z);
  map.attributionControl.setPrefix(false);
  map.attributionControl.addAttribution(OPENFREEMAP_ATTRIBUTION_HTML);
  try {
    baseMapLayer = addOpenFreeMapLayer(map, { label: 'guild-location-picker' });
  } catch (error) {
    console.warn('[MapPicker] OpenFreeMap indisponible', error);
    mapError.value = 'Le fond de carte est indisponible. La sélection du point reste utilisable.';
  }
  if (playerLoc) {
    const playerIcon = L.divIcon({
      className: 'custom-map-icon',
      html: `<div class="map-player-marker">🧍</div>`,
      iconSize: [24, 36],
      iconAnchor: [12, 36]
    });
    L.marker(playerLoc, { icon: playerIcon, zIndexOffset: -1000 }).addTo(map);
  }
  if (hasSelection) {
    marker = createMarker(props.modelValue.lat, props.modelValue.lng);
    if (props.resolveTerritory && (!props.modelValue?.country || !props.modelValue?.region)) {
      void selectCoordinates(props.modelValue.lat, props.modelValue.lng);
    }
  }
  if (props.journeyPoints.length) { renderJourney(); fitJourney(); }
  map.on('zoomend', () => { if(props.journeyPoints.length) renderJourney(); });
  map.on('click', (e: any) => { selectCoordinates(e.latlng.lat, e.latlng.lng); });
  resizeObserver = new ResizeObserver(() => {
    if (!map) return;
    map.invalidateSize();
    if (props.journeyPoints.length && !journeyFramed) fitJourney();
    if (props.journeyPoints.length) renderJourney();
  });
  resizeObserver.observe(mapContainer.value);
  // Gardé : si le composant est démonté avant les 100 ms (changement d'onglet),
  // `map` est déjà null → c'était l'erreur « reading 'invalidateSize' ».
  setTimeout(() => { if (map) map.invalidateSize(); }, 100);
}

async function reverseTerritory(lat: number, lng: number) {
  const serverResponse = await new Promise<any>((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, 2_500);
    socket.emit('map:reverseGeocode', { lat, lng }, (response: any) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(response?.success ? response : null);
    });
  });
  return serverResponse || reverseTerritoryFromCoordinates(lat, lng);
}

function selectCoordinates(lat: number, lng: number) {
  if (!props.resolveTerritory) {
    emit('update:modelValue', { ...(props.modelValue || {}), lat, lng });
    return;
  }
  const requestId = ++reverseRequestId;
  // Le marqueur réagit immédiatement ; le territoire arrive ensuite depuis la
  // file serveur mise en cache, sans demander aucune saisie au créateur.
  emit('update:modelValue', {
    ...(props.modelValue || {}),
    lat,
    lng,
    region: '',
    country: '',
    label: '',
  });
  if (reverseTimer) clearTimeout(reverseTimer);
  reverseTimer = setTimeout(async () => {
    reverseTimer = null;
    const territory = await reverseTerritory(lat, lng);
    if (!territory || requestId !== reverseRequestId) return;
    emit('update:modelValue', {
      ...(props.modelValue || {}),
      lat,
      lng,
      region: territory.region || '',
      country: territory.country || '',
      label: territory.label || '',
    });
  }, 350);
}

function createMarker(lat: number, lng: number) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><ellipse cx="16" cy="40" rx="6" ry="2.5" fill="rgba(0,0,0,0.4)"/><path d="M16 41 C6 30 2 21 2 14 A14 14 0 0 1 30 14 C30 21 26 30 16 41 Z" fill="#1a1a2e" stroke="#e6c288" stroke-width="1.5"/><circle cx="16" cy="14" r="10.5" fill="#16213e"/><g transform="translate(4,2)" fill="none" stroke="#e6c288" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 5V3"/><path d="M14 5V3"/><path d="M15 21v-3a3 3 0 0 0-6 0v3"/><path d="M18 3v8"/><path d="M18 5H6"/><path d="M22 11H2"/><path d="M22 9v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9"/><path d="M6 3v8"/></g></svg>`;
  const icon = L.divIcon({
    className: 'custom-map-icon map-pin',
    html: svg,
    iconSize: [32, 42],
    iconAnchor: [16, 41]
  });
  return L.marker([lat, lng], { icon }).addTo(map);
}

async function searchAddress() {
  if (!searchText.value.trim()) return;
  searching.value = true;
  try {
    const q = encodeURIComponent(searchText.value.trim());
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${q}&limit=1`, {
      headers: { 'Accept-Language': 'fr' },
      referrerPolicy: 'strict-origin-when-cross-origin',
    });
    const data = await res.json();
    if (data && data.length > 0) {
      const { lat, lon, address = {}, display_name: displayName } = data[0];
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lon);
      const region = address.state || address.region || address.county || address.city || '';
      emit('update:modelValue', {
        ...(props.modelValue || {}),
        lat: latNum,
        lng: lngNum,
        region,
        country: address.country || '',
        label: displayName || '',
      });
      if (map) map.setView([latNum, lngNum], 18, { animate: true });
    } else {
      window.dispatchEvent(new CustomEvent('guild-toast', { detail: { msg: 'Adresse introuvable.', type: 'error' } }));
    }
  } catch (e) {
    window.dispatchEvent(new CustomEvent('guild-toast', { detail: { msg: 'Erreur de recherche d\'adresse.', type: 'error' } }));
  } finally {
    searching.value = false;
  }
}

function locateMe() {
  if (!map) return;
  const playerLoc = gpsState.status === 'ok' ? gpsState.location : null;
  if (playerLoc) {
    void selectCoordinates(playerLoc[0], playerLoc[1]);
    map.setView(playerLoc, 18, { animate: true });
  } else {
    window.dispatchEvent(new CustomEvent('guild-toast', { detail: { msg: 'Position GPS non disponible.', type: 'error' } }));
  }
}
</script>

<style scoped>
.map-picker-root { display: flex; flex-direction: column; gap: 8px; }
.map-search { display: flex; gap: 8px; }
.map-search-input { flex: 1; min-width: 0; }
.map-overview { align-self: flex-start; min-height: 44px; padding: 8px 12px; color: #56391d; background: #f7ebce; border: 1px solid #8c6738; border-radius: 7px; font-weight: 700; cursor: pointer; flex-shrink: 0; }
.map-container { width: 100%; height: 260px; border-radius: 14px; border: 1px solid var(--dark-border); background: #1a1a1a; overflow: hidden; }
.map-hint { font-size: 0.7rem; color: var(--gold-dim); min-height: 1.2em; }
.map-error { font-size: 0.75rem; color: var(--red); text-align: center; padding: 8px; background: rgba(255,0,0,0.08); border-radius: 10px; }
@media (max-width: 480px) {
  .map-search { flex-wrap: wrap; }
  .map-search-input { flex: 1 1 100%; }
}
</style>

<style>
.custom-map-icon { background: transparent; border: none; }
.journey-map-pin { background: transparent; border: 0; }
.journey-map-badge { display: grid; place-items: center; width: 44px; height: 44px; box-sizing: border-box; border: 3px solid #f7e5b3; border-radius: 50%; background: #75352e; color: #fff8e5; font: 700 20px/1 Georgia, serif; box-shadow: 0 2px 5px #0009; }
.journey-map-badge.is-current { background: #205853; outline: 3px solid #205853; outline-offset: 2px; }
.journey-map-pin:focus-visible { outline: 3px solid #205853; outline-offset: 5px; border-radius: 50%; }
.map-player-marker { font-size: 18px; filter: drop-shadow(0 0 4px rgba(0,0,0,0.8)); }
.map-pin svg { display: block; width: 30px; height: 40px; overflow: visible; filter: drop-shadow(0 0 6px var(--gold)); }
.leaflet-grab { cursor: url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%2724%27%20height%3D%2724%27%20viewBox%3D%270%200%2024%2024%27%3E%3Cpath%20d%3D%27M20%2020L4%204%27%20stroke%3D%27%23e6c288%27%20stroke-width%3D%272.5%27%20stroke-linecap%3D%27round%27%2F%3E%3Cpath%20d%3D%27M4%204L2%202L6%203L7%207L3%206Z%27%20fill%3D%27%23e6c288%27%20stroke%3D%27%23e6c288%27%20stroke-width%3D%271.5%27%20stroke-linejoin%3D%27round%27%2F%3E%3Cpath%20d%3D%27M18%2018L16%2014L14%2016L18%2018Z%27%20fill%3D%27%23e6c288%27%2F%3E%3C%2Fsvg%3E") 2 2, grab; }
.leaflet-grabbing { cursor: url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%2724%27%20height%3D%2724%27%20viewBox%3D%270%200%2024%2024%27%3E%3Cpath%20d%3D%27M20%2020L4%204%27%20stroke%3D%27%23e6c288%27%20stroke-width%3D%272.5%27%20stroke-linecap%3D%27round%27%2F%3E%3Cpath%20d%3D%27M4%204L2%202L6%203L7%207L3%206Z%27%20fill%3D%27%23e6c288%27%20stroke%3D%27%23e6c288%27%20stroke-width%3D%271.5%27%20stroke-linejoin%3D%27round%27%2F%3E%3Cpath%20d%3D%27M18%2018L16%2014L14%2016L18%2018Z%27%20fill%3D%27%23e6c288%27%2F%3E%3C%2Fsvg%3E") 2 2, grabbing; }
</style>
